import {
    DepartmentSortKey,
    PreOpsAnswerSortKey,
    PreOpsChecklistSortKey,
    PreOpsQuestionSortKey,
    SafetyMessageSortKey,
    ShiftSortKey,
    ShockEventSortKey,
    ShockProfileSortKey,
    SiteSortKey,
    UserSortKey,
    UserGroupAssignmentSortKey,
    UserGroupSortKey,
    VehicleDeviceSortKey,
    VehicleSortKey,
    VehicleGroupAssignmentSortKey,
    VehicleGroupSortKey,
    FleetAuthenticatePeriodSortKey,
    VehicleMovementPeriodSortKey,
    PhoneSortKey,
    VehicleTypeSortKey,
    MotionSensorSortKey,
    PedestrianDeviceSortKey,
    EloshieldClassicStationaryModuleSortKey,
    StationaryUnitSortKey,
} from "../../api";
import { ListStates as ListStates } from "../../utils/list-states";
import type { RepositoryDepartments } from "../repositories/repository-departments";
import type { RepositoryFleetAuthenticatePeriods } from "../repositories/repository-fleet-authenticate-periods";
import type { RepositoryPreOpsAnswers } from "../repositories/repository-pre-ops-answers";
import type { RepositoryPreOpsChecklists } from "../repositories/repository-pre-ops-checklists";
import type { RepositoryPreOpsQuestions } from "../repositories/repository-pre-ops-questions";
import type { RepositorySafetyMessages } from "../repositories/repository-safety-messages";
import type { RepositoryShifts } from "../repositories/repository-shifts";
import type { RepositoryShockEvents } from "../repositories/repository-shock-events";
import type { RepositoryShockProfiles } from "../repositories/repository-shock-profiles";
import type { RepositorySites } from "../repositories/repository-sites";
import type { RepositoryUserGroupAssignments } from "../repositories/repository-user-group-assignments";
import type { RepositoryUserGroups } from "../repositories/repository-user-groups";
import type { RepositoryUsers } from "../repositories/repository-users";
import type { RepositoryVehicleDevices } from "../repositories/repository-vehicle-devices";
import type { RepositoryVehicleGroupAssignments } from "../repositories/repository-vehicle-group-assignments";
import type { RepositoryVehicleGroups } from "../repositories/repository-vehicle-groups";
import type { RepositoryVehicleMovementPeriods } from "../repositories/repository-vehicle-movement-periods";
import type { RepositoryVehicles } from "../repositories/repository-vehicles";
import type { RepositoryPhones } from "../repositories/repository-phones";
import type { RepositoryVehicleTypes } from "../repositories/repository-vehicle-types";
import type { RepositoryMotionSensors } from "../repositories/repository-motion-sensors";
import type { HistoryProvider } from "../providers/history-provider";
import type { RepositoryPedestrianDevices } from "../repositories/repository-pedestrian-devices";
import type { RepositoryEloshieldClassicStationaryModules } from "../repositories/repository-eloshield-classic-stationary-modules";
import type { RepositoryStationaryUnits } from "../repositories/repository-stationary-units";

/**
 * Hosts all lists states for all lists in the application.
 * This service is initialized when the application starts up.
 * List components can inject this service and register new states
 * on their corresponding list states member.
 */

export class ServiceListStates {
    constructor(
        private readonly historyProvider: HistoryProvider,
        private readonly repositoryDepartments: RepositoryDepartments,
        private readonly repositoryMotionSensors: RepositoryMotionSensors,
        private readonly repositoryPreOpsChecklists: RepositoryPreOpsChecklists,
        private readonly repositoryPreOpsQuestions: RepositoryPreOpsQuestions,
        private readonly repositoryShifts: RepositoryShifts,
        private readonly repositoryShockEvents: RepositoryShockEvents,
        private readonly repositoryShockProfiles: RepositoryShockProfiles,
        private readonly repositorySafetyMessages: RepositorySafetyMessages,
        private readonly repositorySites: RepositorySites,
        private readonly repositoryUserGroupAssignments: RepositoryUserGroupAssignments,
        private readonly repositoryUserGroups: RepositoryUserGroups,
        private readonly repositoryUsers: RepositoryUsers,
        private readonly repositoryVehicleDevices: RepositoryVehicleDevices,
        private readonly repositoryVehicleGroupAssignments: RepositoryVehicleGroupAssignments,
        private readonly repositoryVehicleGroups: RepositoryVehicleGroups,
        private readonly repositoryVehicleMovementPeriods: RepositoryVehicleMovementPeriods,
        private readonly repositoryVehicleTypes: RepositoryVehicleTypes,
        private readonly repositoryVehicles: RepositoryVehicles,
        private readonly repositoryPedestrianDevices: RepositoryPedestrianDevices,
        private readonly repositoryPhones: RepositoryPhones,
        private readonly repositoryPreOpsAnswers: RepositoryPreOpsAnswers,
        private readonly repositoryFleetAuthenticatePeriods: RepositoryFleetAuthenticatePeriods,
        private readonly repositoryStationaryUnits: RepositoryStationaryUnits,
        private readonly RepositoryEloshieldClassicStationaryModules: RepositoryEloshieldClassicStationaryModules,
    ) {}

    /** States for all {@link ListDepartments}. */
    public departments = new ListStates<RepositoryDepartments>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "label":
                        return DepartmentSortKey.LABEL;
                    case "createdAt":
                        return DepartmentSortKey.CREATED_AT;
                }
            },
            repository: () => this.repositoryDepartments,
        },
        this.historyProvider,
    );

    /** States for all {@link ListMotionSensors}. */
    public motionSensors = new ListStates<RepositoryMotionSensors>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "vehicleDeviceSerialNumber":
                        return MotionSensorSortKey.VEHICLE_DEVICE_SERIAL_NUMBER;
                    case "firmwareLastUpdatedAt":
                        return MotionSensorSortKey.FIRMWARE_LAST_UPDATED_AT;
                    case "currentFirmwareVersion":
                        return MotionSensorSortKey.CURRENT_VERSION;
                }
            },
            repository: () => this.repositoryMotionSensors,
        },
        this.historyProvider,
    );

    /** States for all {@link ListPreOpsChecklists}. */
    public preOpsChecklists = new ListStates<RepositoryPreOpsChecklists>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "createdAt":
                        return PreOpsChecklistSortKey.CREATED_AT;
                    case "label":
                        return PreOpsChecklistSortKey.LABEL;
                }
            },
            repository: () => this.repositoryPreOpsChecklists,
        },
        this.historyProvider,
    );

    /** States for all {@link ListPreOpsQuestions}. */
    public preOpsQuestions = new ListStates<RepositoryPreOpsQuestions>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "position":
                        return PreOpsQuestionSortKey.POSITION;
                    case "body":
                        return PreOpsQuestionSortKey.BODY;
                }
            },
            repository: () => this.repositoryPreOpsQuestions,
        },
        this.historyProvider,
    );

    /** States for all {@link ListShifts}. */
    public shifts = new ListStates<RepositoryShifts>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "label":
                        return ShiftSortKey.LABEL;
                    case "createdAt":
                        return ShiftSortKey.CREATED_AT;
                }
            },
            repository: () => this.repositoryShifts,
        },
        this.historyProvider,
    );

    /** States for all {@link ListShockEvents}. */
    public shockEvents = new ListStates<RepositoryShockEvents>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "time":
                        return ShockEventSortKey.TIME;
                    case "username":
                        return ShockEventSortKey.USERNAME;
                    case "severity":
                        return ShockEventSortKey.G_FORCE;
                    case "vehicle":
                        return ShockEventSortKey.VEHICLE;
                    case "department":
                        return ShockEventSortKey.VEHICLE_DEPARTMENT;
                }
            },

            repository: () => this.repositoryShockEvents,
        },
        this.historyProvider,
    );

    /** States for all {@link ListShockProfiles}. */
    public shockProfiles = new ListStates<RepositoryShockProfiles>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "createdAt":
                        return ShockProfileSortKey.CREATED_AT;
                    case "label":
                        return ShockProfileSortKey.LABEL;
                    case "light":
                        return ShockProfileSortKey.LIGHT;
                    case "medium":
                        return ShockProfileSortKey.MEDIUM;
                    case "severe":
                        return ShockProfileSortKey.SEVERE;
                    case "duration":
                        return ShockProfileSortKey.DURATION;
                }
            },
            repository: () => this.repositoryShockProfiles,
        },
        this.historyProvider,
    );

    /** States for all {@link ListSafetyMessages}. */
    public safetyMessages = new ListStates<RepositorySafetyMessages>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "body":
                        return SafetyMessageSortKey.BODY;
                    case "createdAt":
                        return SafetyMessageSortKey.CREATED_AT;
                }
            },
            repository: () => this.repositorySafetyMessages,
        },
        this.historyProvider,
    );

    /** States for all {@link ListSites}. */
    public sites = new ListStates<RepositorySites>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "label":
                        return SiteSortKey.LABEL;
                    case "createdAt":
                        return SiteSortKey.CREATED_AT;
                }
            },
            repository: () => this.repositorySites,
        },
        this.historyProvider,
    );

    /** States for all {@link ListUserGroupAssignments}. */
    public userGroupAssignments = new ListStates<RepositoryUserGroupAssignments>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "createdAt":
                        return UserGroupAssignmentSortKey.CREATED_AT;
                }
            },
            repository: () => this.repositoryUserGroupAssignments,
        },
        this.historyProvider,
    );

    /** States for all {@link ListUserGroups}. */
    public userGroups = new ListStates<RepositoryUserGroups>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "label":
                        return UserGroupSortKey.LABEL;
                    case "createdAt":
                        return UserGroupSortKey.CREATED_AT;
                }
            },
            repository: () => this.repositoryUserGroups,
        },
        this.historyProvider,
    );

    /** States for all {@link ListUsers}. */
    public users = new ListStates<RepositoryUsers>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "createdAt":
                        return UserSortKey.CREATED_AT;
                    case "firstName":
                        return UserSortKey.FIRST_NAME;
                    case "lastName":
                        return UserSortKey.LAST_NAME;
                    case "jobTitle":
                        return UserSortKey.JOB_TITLE;
                    case "email":
                        return UserSortKey.EMAIL;
                    case "managementRole":
                        return UserSortKey.MANAGEMENT_ROLE;
                    case "fleetRole":
                        return UserSortKey.FLEET_ROLE;
                    case "department":
                        return UserSortKey.DEPARTMENT;
                    case "shift":
                        return UserSortKey.SHIFT;
                    case "site":
                        return UserSortKey.SITE;
                    case "employeeId":
                        return UserSortKey.EMPLOYEE_ID;
                    case "licenseExpiryDate":
                        return UserSortKey.LICENSE_EXPIRY_DATE;
                    case "expiryLogout":
                        return UserSortKey.EXPIRY_LOGOUT;
                }
            },
            repository: () => this.repositoryUsers,
        },
        this.historyProvider,
    );

    /** States for all {@link ListUserProductivity}. */
    public userProductivity = new ListStates<RepositoryUsers>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "createdAt":
                        return UserSortKey.CREATED_AT;
                    case "firstName":
                        return UserSortKey.FIRST_NAME;
                    case "lastName":
                        return UserSortKey.LAST_NAME;
                    case "jobTitle":
                        return UserSortKey.JOB_TITLE;
                    case "email":
                        return UserSortKey.EMAIL;
                    case "managementRole":
                        return UserSortKey.MANAGEMENT_ROLE;
                    case "fleetRole":
                        return UserSortKey.FLEET_ROLE;
                    case "department":
                        return UserSortKey.DEPARTMENT;
                    case "shift":
                        return UserSortKey.SHIFT;
                    case "site":
                        return UserSortKey.SITE;
                    case "employeeId":
                        return UserSortKey.EMPLOYEE_ID;
                    case "licenseExpiryDate":
                        return UserSortKey.LICENSE_EXPIRY_DATE;
                    case "expiryLogout":
                        return UserSortKey.EXPIRY_LOGOUT;
                }
            },
            repository: () => this.repositoryUsers,
        },
        this.historyProvider,
    );

    /** States for all {@link ListVehicleDevices}. */
    public vehicleDevices = new ListStates<RepositoryVehicleDevices>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "serialNumber":
                        return VehicleDeviceSortKey.SERIAL_NUMBER;
                    case "createdAt":
                        return VehicleDeviceSortKey.CREATED_AT;
                }
            },
            repository: () => this.repositoryVehicleDevices,
        },
        this.historyProvider,
    );

    /** States for all {@link ListVehicleGroupAssignments}. */
    public vehicleGroupAssignments = new ListStates<RepositoryVehicleGroupAssignments>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "createdAt":
                        return VehicleGroupAssignmentSortKey.CREATED_AT;
                }
            },
            repository: () => this.repositoryVehicleGroupAssignments,
        },
        this.historyProvider,
    );

    /** States for all {@link ListVehicleGroups}. */
    public vehicleGroups = new ListStates<RepositoryVehicleGroups>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "label":
                        return VehicleGroupSortKey.LABEL;
                    case "createdAt":
                        return VehicleGroupSortKey.CREATED_AT;
                }
            },
            repository: () => this.repositoryVehicleGroups,
        },
        this.historyProvider,
    );

    /** States for all {@link ListVehicleMovementPeriods}. */
    public vehicleMovementPeriods = new ListStates<RepositoryVehicleMovementPeriods>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "eventId":
                        return VehicleMovementPeriodSortKey.EVENT_ID;
                    case "vehicleSerialNumber":
                        return VehicleMovementPeriodSortKey.VEHICLE_SERIAL_NUMBER;
                    case "userEmployeeId":
                        return VehicleMovementPeriodSortKey.USER_EMPLOYEE_ID;
                    case "startTime":
                        return VehicleMovementPeriodSortKey.START_TIME;
                    case "stopTime":
                        return VehicleMovementPeriodSortKey.STOP_TIME;
                    case "totalTime":
                        return VehicleMovementPeriodSortKey.TOTAL_TIME;
                }
            },
            repository: () => this.repositoryVehicleMovementPeriods,
        },
        this.historyProvider,
    );

    /** States for all {@link ListVehicleTypes}. */
    public vehicleTypes = new ListStates<RepositoryVehicleTypes>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "label":
                        return VehicleTypeSortKey.LABEL;
                }
            },
            repository: () => this.repositoryVehicleTypes,
        },
        this.historyProvider,
    );

    /** States for all {@link ListVehicles}. */
    public vehicles = new ListStates<RepositoryVehicles>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "serialNumber":
                        return VehicleSortKey.SERIAL_NUMBER;
                    case "createdAt":
                        return VehicleSortKey.CREATED_AT;
                    case "manufactureYear":
                        return VehicleSortKey.MANUFACTURE_YEAR;
                    case "manufacturer":
                        return VehicleSortKey.MANUFACTURER;
                    case "model":
                        return VehicleSortKey.MODEL;
                    case "department":
                        return VehicleSortKey.DEPARTMENT;
                    case "shockProfile":
                        return VehicleSortKey.SHOCK_PROFILE;
                    case "phoneImei":
                        return VehicleSortKey.PHONE_IMEI;
                    case "procurementType":
                        return VehicleSortKey.PROCUREMENT_TYPE;
                    case "monthlyCost":
                        return VehicleSortKey.MONTHLY_COST;
                    case "purchaseCost":
                        return VehicleSortKey.PURCHASE_COST;
                }
            },
            repository: () => this.repositoryVehicles,
        },
        this.historyProvider,
    );

    /** States for all {@link Phones} */
    public pedestrianDevices = new ListStates<RepositoryPedestrianDevices>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "createdAt":
                        return PedestrianDeviceSortKey.CREATED_AT;
                    case "label":
                        return PedestrianDeviceSortKey.LABEL;
                    case "networkId":
                        return PedestrianDeviceSortKey.NETWORK_ID;
                }
            },
            repository: () => this.repositoryPedestrianDevices,
        },
        this.historyProvider,
    );

    /** States for all {@link StationaryUnits} */
    public stationaryUnits = new ListStates<RepositoryStationaryUnits>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "createdAt":
                        return StationaryUnitSortKey.CREATED_AT;
                    case "serialNumber":
                        return StationaryUnitSortKey.SERIAL_NUMBER;
                }
            },
            repository: () => this.repositoryStationaryUnits,
        },
        this.historyProvider,
    );

    /** States for all {@link EloshieldClassicStationaryModules} */
    public eloshieldClassicStationaryModules =
        new ListStates<RepositoryEloshieldClassicStationaryModules>(
            {
                getSortKeyForColumn: (column) => {
                    switch (column.key) {
                        case "label":
                            return EloshieldClassicStationaryModuleSortKey.LABEL;
                    }
                },
                repository: () => this.RepositoryEloshieldClassicStationaryModules,
            },
            this.historyProvider,
        );

    /** States for all {@link Phones} */
    public phones = new ListStates<RepositoryPhones>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "createdAt":
                        return PhoneSortKey.CREATED_AT;
                    case "appLastUpdatedAt":
                        return PhoneSortKey.APP_LAST_UPDATED_AT;
                }
            },
            repository: () => this.repositoryPhones,
        },
        this.historyProvider,
    );

    /** States for all {@link ListPreOpsAnswers} */
    public preOpsAnswers = new ListStates<RepositoryPreOpsAnswers>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "time":
                        return PreOpsAnswerSortKey.TIME;
                    case "checklistLabel":
                        return PreOpsAnswerSortKey.CHECKLIST;
                    case "question":
                        return PreOpsAnswerSortKey.QUESTION;
                    case "user":
                        return PreOpsAnswerSortKey.USERNAME;
                    case "vehicle":
                        return PreOpsAnswerSortKey.VEHICLE;
                    case "department":
                        return PreOpsAnswerSortKey.VEHICLE_DEPARTMENT;
                }
            },
            repository: () => this.repositoryPreOpsAnswers,
        },
        this.historyProvider,
    );

    /** States for all {@link ListLoginReport}. */
    public fleetAuthenticatePeriod = new ListStates<RepositoryFleetAuthenticatePeriods>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "eventId":
                        return FleetAuthenticatePeriodSortKey.EVENT_ID;
                    case "userEmployeeId":
                        return FleetAuthenticatePeriodSortKey.USER_EMPLOYEE_ID;
                    case "vehicleSerialNumber":
                        return FleetAuthenticatePeriodSortKey.VEHICLE_SERIAL_NUMBER;
                    case "loggedInTime":
                        return FleetAuthenticatePeriodSortKey.LOGGED_IN_TIME;
                    case "loggedOutTime":
                        return FleetAuthenticatePeriodSortKey.LOGGED_OUT_TIME;
                }
            },
            repository: () => this.repositoryFleetAuthenticatePeriods,
        },
        this.historyProvider,
    );

    /** States for all {@link ListVehicleProductivity}. */
    public vehicleProductivity = new ListStates<RepositoryVehicles>(
        {
            getSortKeyForColumn: (column) => {
                switch (column.key) {
                    case "serialNumber":
                        return VehicleSortKey.SERIAL_NUMBER;
                    case "createdAt":
                        return VehicleSortKey.CREATED_AT;
                    case "manufactureYear":
                        return VehicleSortKey.MANUFACTURE_YEAR;
                    case "manufacturer":
                        return VehicleSortKey.MANUFACTURER;
                    case "model":
                        return VehicleSortKey.MODEL;
                    case "department":
                        return VehicleSortKey.DEPARTMENT;
                }
            },
            repository: () => this.repositoryVehicles,
        },
        this.historyProvider,
    );
}
